import { Box, Link, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { TextBlock } from 'common/ui/TextBlock';
import { useConfirmationModal } from 'services/modals/hooks';
import { ToastType, useToast } from 'services/toast';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { formatDate } from 'utils/datetime';
import { Organization } from 'generated-graphql/types';
import {
  useHideOrganizationMutation,
  useUnhideOrganizationMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { accountVisibilitySectionSx } from '../styles';

type AccountVisibilitySectionProps = {
  organization: Organization;
  onViewHiddenAccounts?: () => void;
  onVisibilityChange?: (
    changedOrg: Organization,
    action: 'hide' | 'unhide'
  ) => Promise<void>;
};

export const AccountVisibilitySection: React.FC<
  AccountVisibilitySectionProps
> = ({ organization, onViewHiddenAccounts, onVisibilityChange }) => {
  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();

  const isHidden = organization.isHidden ?? false;
  const hiddenAt = organization.hiddenAt;

  const [hideOrganization] = useHideOrganizationMutation();
  const [unhideOrganization] = useUnhideOrganizationMutation();

  const handleHideAccount = () => {
    showModal({
      title: 'Hide this account?',
      message:
        'Hiding an account does not close conversations and analyses, and your team will still receive notifications.',
      modalType: ConfirmationModalType.DEFAULT,
      confirmationText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        showLoadingOverlay();
        try {
          const { data } = await hideOrganization({
            variables: {
              input: {
                organizationId: organization.id
              }
            }
          });

          if (data?.hideOrganization) {
            const updatedOrg = {
              ...organization,
              isHidden: data.hideOrganization.isHidden,
              hiddenAt: data.hideOrganization.hiddenAt
            };

            await onVisibilityChange?.(updatedOrg, 'hide');

            showToast({
              title: `${organization.name} is now hidden`,
              type: ToastType.Success
            });
          }
        } catch (error) {
          showToast({
            title: 'Something went wrong',
            message: 'Could not hide account. Please try again.',
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const handleUnhideAccount = async () => {
    showLoadingOverlay();
    try {
      const { data } = await unhideOrganization({
        variables: {
          input: {
            organizationId: organization.id
          }
        }
      });

      if (data?.unhideOrganization) {
        const updatedOrg = {
          ...organization,
          isHidden: data.unhideOrganization.isHidden,
          hiddenAt: data.unhideOrganization.hiddenAt
        };

        await onVisibilityChange?.(updatedOrg, 'unhide');

        showToast({
          title: `${organization.name} is visible again`,
          type: ToastType.Success
        });
      }
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: 'Could not make account visible. Please try again.',
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const getSubtitle = () => {
    if (isHidden && hiddenAt) {
      return `This account was hidden on ${formatDate(hiddenAt)}.`;
    }
    return (
      <Typography>
        Hide this account from your accounts list.{' '}
        {onViewHiddenAccounts && (
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewHiddenAccounts();
            }}
          >
            View hidden accounts
          </Link>
        )}
      </Typography>
    );
  };

  return (
    <Box sx={accountVisibilitySectionSx.sectionContainer}>
      <TextBlock
        variant="section"
        title="Account visibility"
        subtitle={getSubtitle()}
      />
      <Box sx={accountVisibilitySectionSx.buttonContainer}>
        {isHidden ? (
          <Button variant="primary" onClick={handleUnhideAccount}>
            Make account visible
          </Button>
        ) : (
          <Button
            variant="primary"
            state="destructive"
            onClick={handleHideAccount}
          >
            Hide account
          </Button>
        )}
      </Box>
    </Box>
  );
};
